import clsx from "clsx";
import { FilterOption } from "@/shared/models/filterTypes";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import {
  highlightTermsInText,
  SEARCH_HIGHLIGHT_COLOR,
} from "@/utils/text-highlight";

type PropTypes = {
  label: string;
  description?: string;
  filters: FilterOption[];
  selected: (number | string)[];
  onToggle: (filterId: number | string) => void;
  emptyLabel?: string;
  className?: string;
  renderItemExtras?: (filterId: number | string) => React.ReactNode;
  renderBodyExtras?: () => React.ReactNode;
  footer?: React.ReactNode;
  disabled?: boolean;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  helperText?: string;
  showCount?: boolean;
  hideHeader?: boolean;
  /** When non-empty, force-expands subsections by default and highlights matched chars in option labels. */
  searchQuery?: string;
  subsections?: {
    id: string;
    label: string;
    filterIds: (number | string)[];
    defaultCollapsed?: boolean;
    createNewLink?: {
      url: string;
      label?: string;
    };
  }[];
  createNewLink?: {
    url: string;
    label?: string;
  };
};

export default function FilterMenu(props: PropTypes) {
  const isDisabled = props.disabled ?? false;
  const [isCollapsed, setIsCollapsed] = useState(
    props.defaultCollapsed ?? false
  );

  // Track collapsed state for each subsection
  const [collapsedSubsections, setCollapsedSubsections] = useState<
    Record<string, boolean>
  >(() => {
    const initial: Record<string, boolean> = {};
    props.subsections?.forEach((sub) => {
      initial[sub.id] = sub.defaultCollapsed ?? false;
    });
    return initial;
  });

  // Search-aware collapse: while a search is active, subsections default to
  // expanded so users can see matches without an extra click. Manual toggles
  // during the search are tracked separately and reset when the search starts
  // or ends — pre-search collapsed state is therefore preserved.
  const isSearching = !!props.searchQuery?.trim();
  const [collapsedDuringSearch, setCollapsedDuringSearch] = useState<
    Record<string, boolean>
  >({});
  const prevSearchRef = useRef<string | undefined>(props.searchQuery);
  useEffect(() => {
    const wasSearching = !!prevSearchRef.current?.trim();
    if (wasSearching !== isSearching) {
      setCollapsedDuringSearch({});
    }
    prevSearchRef.current = props.searchQuery;
  }, [props.searchQuery, isSearching]);

  const toggleSubsection = (subsectionId: string) => {
    if (isSearching) {
      setCollapsedDuringSearch((prev) => ({
        ...prev,
        [subsectionId]: !(prev[subsectionId] ?? false),
      }));
    } else {
      setCollapsedSubsections((prev) => ({
        ...prev,
        [subsectionId]: !prev[subsectionId],
      }));
    }
  };

  const selectedCount = props.showCount
    ? props.filters.filter((f) => props.selected.includes(f.id)).length
    : 0;

  // Render a single filter item as a checkbox row
  const renderFilterItem = (filter: FilterOption) => {
    if (filter.isHeader) {
      return (
        <li
          key={filter.id}
          className="text-2xs font-medium text-gray-500 uppercase tracking-wider pt-2 pb-0.5 first:pt-0"
        >
          {filter.label}
        </li>
      );
    }

    const isItemDisabled = isDisabled || filter.disabled;
    const isChecked = props.selected.includes(filter.id);

    return (
      <li
        key={filter.id}
        className={clsx(
          "flex items-center gap-2 px-1 py-1 rounded cursor-pointer transition-colors text-xs",
          isItemDisabled && "opacity-40 cursor-not-allowed",
          isChecked && !isItemDisabled && "bg-squawk-blue/5"
        )}
        onClick={() => !isItemDisabled && props.onToggle(filter.id)}
      >
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => props.onToggle(filter.id)}
          disabled={isItemDisabled}
          className="size-3.5 rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-0 focus:ring-offset-0 cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        />
        {isSearching && props.searchQuery ? (
          <span
            className={clsx(
              "flex-1 truncate",
              isItemDisabled
                ? "text-gray-500"
                : isChecked
                  ? "text-gray-100"
                  : "text-gray-300"
            )}
            dangerouslySetInnerHTML={{
              __html: highlightTermsInText(
                filter.label,
                [props.searchQuery.trim()],
                SEARCH_HIGHLIGHT_COLOR,
                false
              ),
            }}
          />
        ) : (
          <span
            className={clsx(
              "flex-1 truncate",
              isItemDisabled
                ? "text-gray-500"
                : isChecked
                  ? "text-gray-100"
                  : "text-gray-300"
            )}
          >
            {filter.label}
          </span>
        )}
        {props.renderItemExtras && props.renderItemExtras(filter.id)}
      </li>
    );
  };

  return (
    <section
      className={clsx(
        "min-w-[180px] max-w-[250px] absolute text-gray-200 rounded-md z-40 px-4 py-4 text-xs space-y-3",
        props.className,
        isDisabled && "opacity-40"
      )}
    >
      {/* Header */}
      {!props.hideHeader && (
        <div
          className={clsx(
            props.collapsible &&
              "cursor-pointer hover:opacity-80 transition-opacity"
          )}
          onClick={(e) => {
            if (props.collapsible) {
              e.stopPropagation();
              setIsCollapsed(!isCollapsed);
            }
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              {props.collapsible &&
                (isCollapsed ? (
                  <ChevronRightIcon className="size-3 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronDownIcon className="size-3 text-gray-400 flex-shrink-0" />
                ))}
              <h3
                className={clsx(
                  "text-xs font-medium flex-1 truncate",
                  isDisabled ? "text-gray-500" : "text-gray-300"
                )}
              >
                {props.label}
                {props.showCount && selectedCount > 0 && (
                  <span className="ml-1.5 text-squawk-blue">
                    ({selectedCount})
                  </span>
                )}
              </h3>
            </div>
          </div>
          {props.description && !isCollapsed && (
            <p className="text-2xs text-gray-500 pt-1">{props.description}</p>
          )}
          {props.helperText && (
            <p className="text-2xs text-gray-500 pt-0.5">{props.helperText}</p>
          )}
        </div>
      )}

      {/* Body */}
      {(!props.collapsible || !isCollapsed) && (
        <>
          {/* Render with subsections */}
          {props.subsections ? (
            <div className="space-y-1">
              {props.subsections.map((subsection) => {
                const hasHeaders = props.filters.some(
                  (f) => subsection.filterIds.includes(f.id) && f.isHeader
                );
                const subsectionFilters = hasHeaders
                  ? subsection.filterIds
                      .map((id) => props.filters.find((f) => f.id === id))
                      .filter((f): f is FilterOption => f !== undefined)
                  : props.filters
                      .filter((f) => subsection.filterIds.includes(f.id))
                      .sort((a, b) => a.label.localeCompare(b.label));

                const isSubCollapsed = isSearching
                  ? (collapsedDuringSearch[subsection.id] ?? false)
                  : (collapsedSubsections[subsection.id] ?? false);
                const subsectionSelectedCount = subsectionFilters.filter(
                  (f) => props.selected.includes(f.id) && !f.isHeader
                ).length;

                return (
                  <div key={subsection.id}>
                    {/* Subsection header — flat, not a bordered box */}
                    <button
                      type="button"
                      className="w-full flex items-center gap-1.5 px-1 py-1.5 text-left hover:bg-gray-800/50 transition-colors rounded"
                      onClick={() => toggleSubsection(subsection.id)}
                    >
                      {isSubCollapsed ? (
                        <ChevronRightIcon className="size-3 text-gray-500 flex-shrink-0" />
                      ) : (
                        <ChevronDownIcon className="size-3 text-gray-500 flex-shrink-0" />
                      )}
                      <span className="text-2xs font-medium text-gray-500 uppercase tracking-wider flex-1">
                        {subsection.label}
                      </span>
                      {subsectionSelectedCount > 0 && (
                        <span className="text-2xs text-squawk-blue">
                          {subsectionSelectedCount}
                        </span>
                      )}
                    </button>

                    {/* Subsection items */}
                    {!isSubCollapsed && (
                      <ul className="space-y-0.5 ml-1">
                        {subsectionFilters.length === 0 && (
                          <li className="text-gray-500 text-center text-xs py-2">
                            {props.emptyLabel ?? "No filters available"}
                          </li>
                        )}
                        {subsectionFilters.map(renderFilterItem)}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            /* Regular rendering without subsections */
            <ul className="space-y-0.5">
              {props.filters.length === 0 && (
                <li className="text-gray-500 text-center text-xs py-2">
                  {props.emptyLabel ?? "No filters available"}
                </li>
              )}
              {props.filters
                .sort((a, b) => a.label.localeCompare(b.label))
                .map(renderFilterItem)}
            </ul>
          )}

          {props.renderBodyExtras && props.renderBodyExtras()}

          {props.footer && (
            <div className="border-t border-gray-700/30 pt-2 mt-2">
              {props.footer}
            </div>
          )}
        </>
      )}
    </section>
  );
}
